import { merge } from 'lodash';
import type { CustomControlCode, ServiceCode } from '@common/enums';
import type {
    HookData,
    HookDataObject,
    PatientData,
    PatientDataObject,
    ProcessData,
    ProcessDataObject,
    ServiceCodeForCustomControlCode,
    ServiceContext,
} from '@common/interfaces/serviceContext.types';

export function createProcessActivityContextData<
    CustomControlData extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number = 1,
    TServiceCode extends keyof ServiceContext | undefined = TCustomControlCode extends CustomControlCode
        ? ServiceCodeForCustomControlCode<TCustomControlCode>
        : undefined,
>({
    customControlData,
    hookData,
    patientData,
    processData,
}: {
    customControlData: CustomControlData;
    hookData?: TCustomControlCode extends CustomControlCode ? HookData<TCustomControlCode, Version> : undefined;
    patientData?: TServiceCode extends ServiceCode ? PatientData<TServiceCode> : undefined;
    processData?: TServiceCode extends ServiceCode ? ProcessData<TServiceCode> : undefined;
}): TServiceCode extends undefined
    ? CustomControlData
    : CustomControlData &
          HookDataObject<TCustomControlCode, Version> &
          PatientDataObject<TServiceCode> &
          ProcessDataObject<TServiceCode> {
    const processActivityContextData = {};

    if (hookData) {
        merge(processActivityContextData, { hookData });
    }
    if (patientData) {
        merge(processActivityContextData, { patientData });
    }
    if (processData) {
        merge(processActivityContextData, { processData });
    }

    return merge(processActivityContextData, customControlData) as TServiceCode extends undefined
        ? CustomControlData
        : CustomControlData &
              HookDataObject<TCustomControlCode, Version> &
              PatientDataObject<TServiceCode> &
              ProcessDataObject<TServiceCode>;
}
